import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import ActivityTracker from './ActivityTracker';
import CSVUploader from '../CSVUploader';
import ProperCSVUploader from '../ProperCSVUploader';

const AdminDashboard = () => {
  const { currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [taskLogs, setTaskLogs] = useState([]);
  const [errorLogs, setErrorLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [logView, setLogView] = useState('detailed'); // 'detailed' or 'slim'
  
  // Backup state
  const [backupConfig, setBackupConfig] = useState(null);
  const [backupList, setBackupList] = useState([]);
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupRunning, setBackupRunning] = useState(false);
  const [backupMessage, setBackupMessage] = useState(null);
  
  // Cleanup state
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);

  // Sync sold vehicles (fix vehicles with completed deals that still show in inventory)
  const [syncSoldRunning, setSyncSoldRunning] = useState(false);
  const [syncSoldResult, setSyncSoldResult] = useState(null);
  const [markSoldVin, setMarkSoldVin] = useState('');
  const [markSoldRunning, setMarkSoldRunning] = useState(false);
  const [restoreVin, setRestoreVin] = useState('');
  const [restoreRunning, setRestoreRunning] = useState(false);
  const [deletedVehicles, setDeletedVehicles] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch users and audit logs separately to prevent one failure from blocking the other
        const usersData = await api.getUsers().catch(err => {
          console.error('Failed to fetch users:', err);
          return [];
        });
        const logsData = await api.getAuditLogs({ limit: 50 }).catch(err => {
          console.error('Failed to fetch audit logs:', err);
          return [];
        });
        const taskLogsData = await api.getTaskLogs().catch(err => {
          console.error('Failed to fetch task logs:', err);
          return [];
        });
        const errorLogsData = await api.request('/auth/error-logs?limit=100').catch(err => {
          console.error('Failed to fetch error logs:', err);
          return [];
        });
        
        setUsers(Array.isArray(usersData) ? usersData : []);
        setAuditLogs(Array.isArray(logsData) ? logsData : []);
        setTaskLogs(Array.isArray(taskLogsData) ? taskLogsData : []);
        setErrorLogs(Array.isArray(errorLogsData) ? errorLogsData : []);
        
        // Fetch backup info
        const configData = await api.request('/backup/config').catch(() => null);
        const backupsData = await api.request('/backup/supabase').catch(() => ({ backups: [] }));
        setBackupConfig(configData);
        setBackupList(backupsData?.backups || []);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const adminCount = users.filter(u => u.role === 'admin').length;
  const userCount = users.filter(u => u.role !== 'admin').length;

  const handleCleanupOrphans = async () => {
    if (!window.confirm('This will remove all orphaned records from deleted vehicles. Continue?')) return;
    setCleanupRunning(true);
    setCleanupResult(null);
    try {
      const result = await api.cleanupOrphanedRecords();
      setCleanupResult(result);
      alert(`Cleanup complete! Removed ${result.details ? Object.values(result.details).reduce((a, b) => a + b, 0) : 0} orphaned records.`);
    } catch (error) {
      alert('Cleanup failed: ' + error.message);
    } finally {
      setCleanupRunning(false);
    }
  };

  const handleSyncSoldStatus = async () => {
    if (!window.confirm('Fix vehicles that have completed deals but still show in inventory? This will mark them as sold.')) return;
    setSyncSoldRunning(true);
    setSyncSoldResult(null);
    try {
      const result = await api.syncSoldStatus();
      setSyncSoldResult(result);
      alert(`Fixed ${result.fixed || 0} vehicle(s). Refresh Inventory to see changes.`);
    } catch (error) {
      alert('Sync failed: ' + error.message);
    } finally {
      setSyncSoldRunning(false);
    }
  };

  const handleRestoreByVin = async () => {
    const vin = restoreVin.trim();
    if (!vin || vin.length < 10) {
      alert('Enter a valid VIN');
      return;
    }
    if (!window.confirm(`Restore vehicle VIN ${vin}? This will bring back the vehicle and its sale.`)) return;
    setRestoreRunning(true);
    try {
      const result = await api.restoreVehicleByVin(vin);
      alert(`Restored ${result.vehicle?.stockNumber} (${result.vehicle?.year} ${result.vehicle?.make} ${result.vehicle?.model}). The sale is intact.`);
      setRestoreVin('');
      setShowDeleted(false);
      setDeletedVehicles([]);
    } catch (error) {
      alert('Failed: ' + error.message);
    } finally {
      setRestoreRunning(false);
    }
  };

  const handleLoadDeleted = async () => {
    try {
      const list = await api.getDeletedVehicles();
      setDeletedVehicles(list);
      setShowDeleted(true);
    } catch (e) {
      alert('Failed to load: ' + e.message);
    }
  };

  const handleMarkSoldByVin = async () => {
    const vin = markSoldVin.trim();
    if (!vin || vin.length < 10) {
      alert('Enter a valid VIN');
      return;
    }
    if (!window.confirm(`Mark vehicle VIN ${vin} as sold?`)) return;
    setMarkSoldRunning(true);
    try {
      const result = await api.markSoldByVin(vin);
      alert(`Marked ${result.vehicle?.stockNumber} (${result.vehicle?.year} ${result.vehicle?.make} ${result.vehicle?.model}) as sold.`);
      setMarkSoldVin('');
      setSyncSoldResult({ fixed: 1, vehicles: [result.vehicle] });
    } catch (error) {
      alert('Failed: ' + error.message);
    } finally {
      setMarkSoldRunning(false);
    }
  };

  const Icon = ({ name, size = 16, className = '' }) => {
    const paths = {
      users: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
      shield: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></>,
      user: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>,
      plus: <><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>,
      arrow: <><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></>,
      home: <><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></>,
    };
    return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>{paths[name]}</svg>;
  };

  return (
    <div className="min-h-screen bg-[#f8f9fb]" style={{ fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      {/* Header */}
      <header className="bg-white border-b border-[#e2e8f0] shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#3b82f6] to-[#1d4ed8] flex items-center justify-center">
                <span className="text-white font-bold">LS</span>
              </div>
              <div>
                <h1 className="text-xl font-semibold text-[#0f172a]">Admin Dashboard</h1>
                <p className="text-sm text-[#64748b]">Manage users and system settings</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2 h-10 px-4 bg-[#f1f5f9] text-[#475569] rounded-lg hover:bg-[#e2e8f0] transition-colors">
                <Icon name="home" size={16} />
                <span className="text-sm font-medium">Back to App</span>
              </Link>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#3b82f6] to-[#8b5cf6] flex items-center justify-center text-white text-sm font-medium">
                  {currentUser?.name?.[0]}
                </div>
                <div>
                  <p className="text-sm font-medium text-[#0f172a]">{currentUser?.name}</p>
                  <p className="text-xs text-[#64748b] capitalize">{currentUser?.role}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl border border-[#e2e8f0] p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-[#dbeafe] flex items-center justify-center">
                <Icon name="users" size={24} className="text-[#3b82f6]" />
              </div>
            </div>
            <p className="text-3xl font-bold text-[#0f172a]">{users.length}</p>
            <p className="text-sm text-[#64748b] mt-1">Total Users</p>
          </div>
          <div className="bg-white rounded-xl border border-[#e2e8f0] p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-[#fef3c7] flex items-center justify-center">
                <Icon name="shield" size={24} className="text-[#f59e0b]" />
              </div>
            </div>
            <p className="text-3xl font-bold text-[#0f172a]">{adminCount}</p>
            <p className="text-sm text-[#64748b] mt-1">Administrators</p>
          </div>
          <div className="bg-white rounded-xl border border-[#e2e8f0] p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-[#dcfce7] flex items-center justify-center">
                <Icon name="user" size={24} className="text-[#22c55e]" />
              </div>
            </div>
            <p className="text-3xl font-bold text-[#0f172a]">{userCount}</p>
            <p className="text-sm text-[#64748b] mt-1">Regular Users</p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl border border-[#e2e8f0] p-6">
          <h2 className="text-lg font-semibold text-[#0f172a] mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 gap-4">
            <Link
              to="/admin/users"
              className="flex items-center justify-between p-4 bg-[#f8f9fb] rounded-xl hover:bg-[#f1f5f9] transition-colors group"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#dbeafe] flex items-center justify-center">
                  <Icon name="users" size={24} className="text-[#3b82f6]" />
                </div>
                <div>
                  <p className="font-medium text-[#0f172a]">Manage Users</p>
                  <p className="text-sm text-[#64748b]">View, edit, and delete user accounts</p>
                </div>
              </div>
              <Icon name="arrow" size={20} className="text-[#94a3b8] group-hover:text-[#3b82f6] transition-colors" />
            </Link>
            <Link
              to="/admin/users/new"
              className="flex items-center justify-between p-4 bg-[#f8f9fb] rounded-xl hover:bg-[#f1f5f9] transition-colors group"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#dcfce7] flex items-center justify-center">
                  <Icon name="plus" size={24} className="text-[#22c55e]" />
                </div>
                <div>
                  <p className="font-medium text-[#0f172a]">Create User</p>
                  <p className="text-sm text-[#64748b]">Add a new user account</p>
                </div>
              </div>
              <Icon name="arrow" size={20} className="text-[#94a3b8] group-hover:text-[#3b82f6] transition-colors" />
            </Link>
            <button
              onClick={handleCleanupOrphans}
              disabled={cleanupRunning}
              className="flex items-center justify-between p-4 bg-[#fef2f2] rounded-xl hover:bg-[#fee2e2] transition-colors group disabled:opacity-50"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#fecaca] flex items-center justify-center">
                  <span className="text-xl">🧹</span>
                </div>
                <div>
                  <p className="font-medium text-[#0f172a]">{cleanupRunning ? 'Cleaning...' : 'Cleanup Orphans'}</p>
                  <p className="text-sm text-[#64748b]">Remove records from deleted vehicles</p>
                </div>
              </div>
            </button>
            <button
              onClick={handleSyncSoldStatus}
              disabled={syncSoldRunning}
              className="flex items-center justify-between p-4 bg-[#fef3c7] rounded-xl hover:bg-[#fde68a] transition-colors group disabled:opacity-50"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#fcd34d] flex items-center justify-center">
                  <span className="text-xl">🚗</span>
                </div>
                <div>
                  <p className="font-medium text-[#0f172a]">{syncSoldRunning ? 'Syncing...' : 'Fix Sold Vehicles'}</p>
                  <p className="text-sm text-[#64748b]">Mark vehicles with completed deals as sold (removes from inventory)</p>
                </div>
              </div>
            </button>
          </div>
          <div className="mt-4 p-4 bg-[#fef3c7] rounded-xl border border-[#fde68a]">
            <p className="text-sm font-medium text-[#0f172a] mb-2">Mark single vehicle as sold by VIN</p>
            <div className="flex gap-2 flex-wrap">
              <input
                type="text"
                value={markSoldVin}
                onChange={e => setMarkSoldVin(e.target.value)}
                placeholder="Enter VIN (e.g. KNDPMCACXK7512713)"
                className="px-3 py-2 border border-[#e2e8f0] rounded-lg text-sm w-64"
              />
              <button
                onClick={handleMarkSoldByVin}
                disabled={markSoldRunning || !markSoldVin.trim()}
                className="px-4 py-2 bg-[#f59e0b] text-white rounded-lg text-sm font-medium hover:bg-[#d97706] disabled:opacity-50"
              >
                {markSoldRunning ? '...' : 'Mark Sold'}
              </button>
            </div>
          </div>
          <div className="mt-4 p-4 bg-red-50 rounded-xl border border-red-200">
            <p className="text-sm font-bold text-red-800 mb-2">🔧 Restore Deleted Vehicle (accidentally deleted?)</p>
            <p className="text-xs text-red-700 mb-3">If you deleted a vehicle (soft delete), the vehicle and sale are still in the database. Restore it here.</p>
            <div className="flex gap-2 flex-wrap mb-3">
              <input
                type="text"
                value={restoreVin}
                onChange={e => setRestoreVin(e.target.value)}
                placeholder="Enter VIN (e.g. KNDPMCACXK7512713)"
                className="px-3 py-2 border border-red-200 rounded-lg text-sm w-64"
              />
              <button
                onClick={handleRestoreByVin}
                disabled={restoreRunning || !restoreVin.trim()}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
              >
                {restoreRunning ? '...' : 'Restore Vehicle + Sale'}
              </button>
            </div>
            <button onClick={handleLoadDeleted} className="text-sm text-red-600 hover:underline">
              {showDeleted ? 'Hide' : 'Show'} list of deleted vehicles
            </button>
            {showDeleted && deletedVehicles.length > 0 && (
              <ul className="mt-2 text-xs max-h-40 overflow-auto space-y-1">
                {deletedVehicles.map(v => (
                  <li key={v.id} className="flex justify-between items-center py-1 border-b border-red-100">
                    <span>{v.stockNumber || v.stock_number} - {v.year} {v.make} {v.model} (VIN: {v.vin}){v.dealCount > 0 ? ` – has sale` : ''}</span>
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Restore ${v.stockNumber || v.stock_number}?`)) return;
                        try {
                          await api.restoreVehicle(v.id);
                          alert('Restored! Refresh the app to see it.');
                          setDeletedVehicles(prev => prev.filter(x => x.id !== v.id));
                        } catch (e) {
                          alert('Failed: ' + e.message);
                        }
                      }}
                      className="text-red-600 hover:underline ml-2 font-medium"
                    >
                      Restore
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {showDeleted && deletedVehicles.length === 0 && <p className="mt-2 text-xs text-red-600">No soft-deleted vehicles found.</p>}
          </div>
          {cleanupResult && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm font-medium text-green-800">Cleanup Results:</p>
              <div className="grid grid-cols-4 gap-2 mt-2 text-xs">
                {Object.entries(cleanupResult.details || {}).map(([key, value]) => (
                  <div key={key} className="bg-white p-2 rounded">
                    <span className="text-neutral-500">{key}:</span> <span className="font-bold">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {syncSoldResult && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm font-medium text-amber-800">Fixed {syncSoldResult.fixed || 0} vehicle(s)</p>
              {syncSoldResult.vehicles?.length > 0 && (
                <ul className="mt-2 text-xs text-amber-700">
                  {syncSoldResult.vehicles.map((v, i) => (
                    <li key={i}>{v.stock_number || v.stockNumber} - {v.year} {v.make} {v.model} (VIN: {v.vin})</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-[#e2e8f0] mb-6">
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'import', label: '📤 Import CSV' },
            { id: 'errors', label: `🚨 Errors${errorLogs.length > 0 ? ` (${errorLogs.length})` : ''}` },
            { id: 'backups', label: 'Backups' },
            { id: 'tasks', label: 'Task Log' },
            { id: 'activity', label: 'Activity Tracker' },
            { id: 'audit', label: 'Audit Log' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === tab.id ? 'border-[#3b82f6] text-[#3b82f6]' : 'border-transparent text-[#64748b] hover:text-[#0f172a]'}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && (
          <>
            {/* Recent Users */}
            <div className="bg-white rounded-xl border border-[#e2e8f0]">
              <div className="px-6 py-4 border-b border-[#e2e8f0] flex items-center justify-between">
                <h2 className="text-lg font-semibold text-[#0f172a]">Recent Users</h2>
                <Link to="/admin/users" className="text-sm text-[#3b82f6] hover:underline">View all</Link>
              </div>
              <div className="divide-y divide-[#f1f5f9]">
                {users.slice(0, 5).map(user => (
                  <div key={user.id} className="px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#3b82f6] to-[#8b5cf6] flex items-center justify-center text-white font-medium">
                        {user.name[0]}
                      </div>
                      <div>
                        <p className="font-medium text-[#0f172a]">{user.name}</p>
                        <p className="text-sm text-[#64748b]">{user.email}</p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${user.role === 'admin' ? 'bg-[#fef3c7] text-[#92400e]' : 'bg-[#dbeafe] text-[#1d4ed8]'}`}>
                      {user.role}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {activeTab === 'import' && (
          <ProperCSVUploader />
        )}

        {activeTab === 'errors' && (
          <div className="bg-white rounded-xl border border-[#e2e8f0]">
            <div className="px-6 py-4 border-b border-[#e2e8f0] flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-[#0f172a]">🚨 Error Log</h2>
                <p className="text-sm text-[#64748b]">Recent errors with user context</p>
              </div>
              <button
                onClick={async () => {
                  if (window.confirm('Clear errors older than 7 days?')) {
                    try {
                      const result = await api.request('/auth/error-logs', { method: 'DELETE' });
                      alert(`Cleared ${result.deleted} old errors`);
                      const fresh = await api.request('/auth/error-logs?limit=100');
                      setErrorLogs(fresh || []);
                    } catch (err) {
                      alert('Failed to clear: ' + err.message);
                    }
                  }
                }}
                className="px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
              >
                🗑️ Clear Old
              </button>
            </div>
            <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
              {errorLogs.length === 0 ? (
                <div className="px-6 py-12 text-center text-[#64748b]">
                  <p className="text-4xl mb-2">✅</p>
                  <p>No errors logged - everything is running smoothly!</p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-[#f8f9fb]">
                    <tr className="text-left text-xs text-[#64748b] border-b border-[#e2e8f0]">
                      <th className="px-4 py-3 font-medium">When</th>
                      <th className="px-4 py-3 font-medium">User</th>
                      <th className="px-4 py-3 font-medium">Action</th>
                      <th className="px-4 py-3 font-medium">Error</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#f1f5f9]">
                    {errorLogs.map(log => (
                      <tr key={log.id} className="hover:bg-[#fef2f2]">
                        <td className="px-4 py-3 text-xs text-[#64748b] whitespace-nowrap">
                          {new Date(log.created_at).toLocaleString()}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-[#0f172a]">{log.user_name || 'Anonymous'}</div>
                          <div className="text-xs text-[#64748b]">{log.user_role || '-'}</div>
                        </td>
                        <td className="px-4 py-3">
                          <code className="text-xs bg-[#f1f5f9] px-2 py-1 rounded">
                            {log.request_method} {log.request_path}
                          </code>
                        </td>
                        <td className="px-4 py-3 max-w-md">
                          <p className="text-red-600 text-xs truncate" title={log.error_message}>
                            {log.error_message}
                          </p>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {activeTab === 'tasks' && (
          <div className="bg-white rounded-xl border border-[#e2e8f0]">
            <div className="px-6 py-4 border-b border-[#e2e8f0] flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-[#0f172a]">Task Activity Log</h2>
                <p className="text-sm text-[#64748b]">Track all task assignments and completions</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setLogView('slim')}
                  className={`px-3 py-1 text-sm rounded ${logView === 'slim' ? 'bg-[#3b82f6] text-white' : 'bg-[#f1f5f9] text-[#64748b]'}`}
                >
                  Slim
                </button>
                <button
                  onClick={() => setLogView('detailed')}
                  className={`px-3 py-1 text-sm rounded ${logView === 'detailed' ? 'bg-[#3b82f6] text-white' : 'bg-[#f1f5f9] text-[#64748b]'}`}
                >
                  Detailed
                </button>
              </div>
            </div>
            
            {logView === 'slim' ? (
              // Slim view - just the essentials
              <div className="divide-y divide-[#f1f5f9] max-h-[600px] overflow-y-auto">
                {taskLogs.map(log => (
                  <div key={log.id} className="px-6 py-3 flex items-center gap-4">
                    <span className={`w-2 h-2 rounded-full ${log.status === 'completed' ? 'bg-green-500' : 'bg-orange-500'}`}></span>
                    <span className="text-xs text-[#64748b] w-32">{new Date(log.created_at).toLocaleDateString()}</span>
                    <span className="font-medium text-[#0f172a] flex-1 truncate">{log.title}</span>
                    <span className={`text-xs px-2 py-1 rounded ${log.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                      {log.status === 'completed' ? '✓ Done' : 'Pending'}
                    </span>
                  </div>
                ))}
                {taskLogs.length === 0 && (
                  <div className="px-6 py-12 text-center text-[#64748b]">No task logs found</div>
                )}
              </div>
            ) : (
              // Detailed view - full information
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-[#64748b] border-b border-[#e2e8f0] bg-[#f8f9fb]">
                      <th className="px-4 py-3 font-medium">Date</th>
                      <th className="px-4 py-3 font-medium">Task</th>
                      <th className="px-4 py-3 font-medium">Assigned By</th>
                      <th className="px-4 py-3 font-medium">Assigned To</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium">Completed</th>
                      <th className="px-4 py-3 font-medium">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#f1f5f9]">
                    {taskLogs.map(log => (
                      <tr key={log.id} className="hover:bg-[#f8f9fb]">
                        <td className="px-4 py-3 text-xs text-[#64748b]">
                          {new Date(log.created_at).toLocaleString()}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-[#0f172a]">{log.title}</div>
                          {log.description && <div className="text-xs text-[#64748b] mt-1 max-w-[200px] truncate">{log.description}</div>}
                        </td>
                        <td className="px-4 py-3 text-[#0f172a]">{log.created_by_name || '-'}</td>
                        <td className="px-4 py-3 text-[#0f172a]">
                          {log.assigned_to_name || log.assigned_role || '-'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            log.status === 'completed' ? 'bg-[#dcfce7] text-[#166534]' :
                            log.status === 'in_progress' ? 'bg-[#dbeafe] text-[#1d4ed8]' :
                            'bg-[#fef3c7] text-[#92400e]'
                          }`}>
                            {log.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-[#64748b]">
                          {log.completed_at ? new Date(log.completed_at).toLocaleString() : '-'}
                        </td>
                        <td className="px-4 py-3 text-xs text-[#64748b] max-w-[150px] truncate">
                          {log.completion_notes || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {taskLogs.length === 0 && (
                  <div className="px-6 py-12 text-center text-[#64748b]">No task logs found</div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'activity' && <ActivityTracker />}

        {activeTab === 'backups' && (
          <div className="space-y-6">
            {/* Backup Status */}
            <div className="bg-white rounded-xl border border-[#e2e8f0] p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-semibold text-[#0f172a]">Backup Configuration</h2>
                  <p className="text-sm text-[#64748b]">Automated backup schedule and status</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={async () => {
                      setBackupRunning(true);
                      setBackupMessage(null);
                      try {
                        const result = await api.request('/backup/test', { method: 'POST', body: JSON.stringify({ type: 'daily' }) });
                        setBackupMessage({ type: 'success', text: `Daily backup completed in ${result.duration}s` });
                        // Refresh backup list
                        const backupsData = await api.request('/backup/supabase').catch(() => ({ backups: [] }));
                        setBackupList(backupsData?.backups || []);
                      } catch (err) {
                        setBackupMessage({ type: 'error', text: err.message });
                      } finally {
                        setBackupRunning(false);
                      }
                    }}
                    disabled={backupRunning}
                    className="px-4 py-2 bg-[#3b82f6] text-white rounded-lg hover:bg-[#2563eb] disabled:opacity-50 text-sm font-medium"
                  >
                    {backupRunning ? 'Running...' : 'Run Daily Backup'}
                  </button>
                  <button
                    onClick={async () => {
                      setBackupRunning(true);
                      setBackupMessage(null);
                      try {
                        const result = await api.request('/backup/test', { method: 'POST', body: JSON.stringify({ type: 'weekly' }) });
                        setBackupMessage({ type: 'success', text: `Weekly backup completed! Email sent: ${result.emailSent}` });
                        const backupsData = await api.request('/backup/supabase').catch(() => ({ backups: [] }));
                        setBackupList(backupsData?.backups || []);
                      } catch (err) {
                        setBackupMessage({ type: 'error', text: err.message });
                      } finally {
                        setBackupRunning(false);
                      }
                    }}
                    disabled={backupRunning}
                    className="px-4 py-2 bg-[#22c55e] text-white rounded-lg hover:bg-[#16a34a] disabled:opacity-50 text-sm font-medium"
                  >
                    {backupRunning ? 'Running...' : 'Run Weekly Backup + Email'}
                  </button>
                </div>
              </div>
              
              {backupMessage && (
                <div className={`mb-4 p-3 rounded-lg ${backupMessage.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                  {backupMessage.text}
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="font-medium text-[#0f172a] mb-3">Schedule</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between py-2 border-b border-[#f1f5f9]">
                      <span className="text-[#64748b]">Daily Backup</span>
                      <span className="font-medium text-[#0f172a]">{backupConfig?.schedule?.daily || 'Every day at 3:00 AM EST'}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-[#f1f5f9]">
                      <span className="text-[#64748b]">Weekly Report</span>
                      <span className="font-medium text-[#0f172a]">{backupConfig?.schedule?.weekly || 'Friday at 6:00 AM EST'}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-[#f1f5f9]">
                      <span className="text-[#64748b]">Email To</span>
                      <span className="font-medium text-[#0f172a]">{backupConfig?.emailTo || 'chris@thewholesalecars.com'}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="font-medium text-[#0f172a] mb-3">Configuration Status</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between py-2 border-b border-[#f1f5f9]">
                      <span className="text-[#64748b]">Supabase Storage</span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${backupConfig?.supabaseConfigured ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {backupConfig?.supabaseConfigured ? '✓ Connected' : '✗ Not Configured'}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-[#f1f5f9]">
                      <span className="text-[#64748b]">Email (SMTP)</span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${backupConfig?.emailConfigured ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {backupConfig?.emailConfigured ? '✓ Configured' : '⚠ Not Set Up'}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-[#f1f5f9]">
                      <span className="text-[#64748b]">Retention</span>
                      <span className="font-medium text-[#0f172a]">30 daily / 12 weekly</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Backup List */}
            <div className="bg-white rounded-xl border border-[#e2e8f0]">
              <div className="px-6 py-4 border-b border-[#e2e8f0] flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-[#0f172a]">Recent Backups</h2>
                  <p className="text-sm text-[#64748b]">{backupList.length} backups stored in cloud</p>
                </div>
                <button
                  onClick={async () => {
                    setBackupLoading(true);
                    const backupsData = await api.request('/backup/supabase').catch(() => ({ backups: [] }));
                    setBackupList(backupsData?.backups || []);
                    setBackupLoading(false);
                  }}
                  className="text-sm text-[#3b82f6] hover:underline"
                >
                  {backupLoading ? 'Loading...' : 'Refresh'}
                </button>
              </div>
              <div className="divide-y divide-[#f1f5f9] max-h-[400px] overflow-y-auto">
                {backupList.length === 0 ? (
                  <div className="px-6 py-12 text-center text-[#64748b]">
                    No backups found. Run a backup to get started.
                  </div>
                ) : (
                  backupList.map((backup, idx) => (
                    <div key={idx} className="px-6 py-4 flex items-center justify-between hover:bg-[#f8f9fb]">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${backup.type === 'weekly' ? 'bg-green-100' : 'bg-blue-100'}`}>
                          <span className={`text-lg ${backup.type === 'weekly' ? 'text-green-600' : 'text-blue-600'}`}>
                            {backup.name?.endsWith('.pdf') ? '📄' : backup.name?.endsWith('.json') ? '💾' : '📁'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-[#0f172a]">{backup.name}</p>
                          <p className="text-sm text-[#64748b]">
                            {backup.created ? new Date(backup.created).toLocaleString() : 'Unknown date'}
                            {backup.type && <span className={`ml-2 px-2 py-0.5 rounded text-xs ${backup.type === 'weekly' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{backup.type}</span>}
                          </p>
                        </div>
                      </div>
                      <a
                        href={backup.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 bg-[#f1f5f9] text-[#475569] rounded-lg hover:bg-[#e2e8f0] text-sm font-medium"
                      >
                        Download
                      </a>
                    </div>
                  ))
                )}
              </div>
            </div>
            
            {/* Email Setup Instructions */}
            {!backupConfig?.emailConfigured && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                <h3 className="font-semibold text-yellow-800 mb-2">⚠️ Email Not Configured</h3>
                <p className="text-sm text-yellow-700 mb-3">To receive weekly backup reports via email, add these environment variables to Railway:</p>
                <div className="bg-yellow-100 rounded-lg p-4 font-mono text-sm text-yellow-800">
                  <div>SMTP_USER=noreply@thewholesalecars.com</div>
                  <div>SMTP_PASS=your_zoho_app_password</div>
                </div>
                <p className="text-sm text-yellow-700 mt-3">Get your app password from Zoho Mail → Settings → Security → App Passwords</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'audit' && (
          <div className="bg-white rounded-xl border border-[#e2e8f0]">
            <div className="px-6 py-4 border-b border-[#e2e8f0]">
              <h2 className="text-lg font-semibold text-[#0f172a]">System Audit Log</h2>
              <p className="text-sm text-[#64748b]">Track all changes made across the system</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-[#64748b] border-b border-[#e2e8f0] bg-[#f8f9fb]">
                    <th className="px-4 py-3 font-medium">Timestamp</th>
                    <th className="px-4 py-3 font-medium">User</th>
                    <th className="px-4 py-3 font-medium">Action</th>
                    <th className="px-4 py-3 font-medium">Entity</th>
                    <th className="px-4 py-3 font-medium">Field</th>
                    <th className="px-4 py-3 font-medium">Old Value</th>
                    <th className="px-4 py-3 font-medium">New Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#f1f5f9]">
                  {auditLogs.map(log => (
                    <tr key={log.id} className="hover:bg-[#f8f9fb]">
                      <td className="px-4 py-3 text-xs text-[#64748b]">
                        {new Date(log.created_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-[#0f172a]">{log.user_name || 'System'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          log.action === 'create' ? 'bg-[#dcfce7] text-[#166534]' :
                          log.action === 'update' ? 'bg-[#dbeafe] text-[#1d4ed8]' :
                          log.action === 'delete' ? 'bg-[#fee2e2] text-[#991b1b]' :
                          log.action === 'status_change' ? 'bg-[#fef3c7] text-[#92400e]' :
                          'bg-[#f1f5f9] text-[#475569]'
                        }`}>
                          {log.action}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-[#0f172a]">{log.entity_type}</div>
                        <div className="text-xs text-[#64748b]">{log.entity_name}</div>
                      </td>
                      <td className="px-4 py-3 text-[#64748b]">{log.field_name || '-'}</td>
                      <td className="px-4 py-3 text-[#64748b] max-w-[150px] truncate">{log.old_value || '-'}</td>
                      <td className="px-4 py-3 text-[#0f172a] max-w-[150px] truncate">{log.new_value || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {auditLogs.length === 0 && (
                <div className="px-6 py-12 text-center text-[#64748b]">No audit logs found</div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
